import { useState } from 'react';
import Axios from 'axios';
import { API_URL } from '../components/Constants';
import { validateInput } from '../components/AdvancedSearch/AdvancedSearchHelpers';
import { isEmpty } from 'lodash';

const useFetchSuggestions = (searchType, selectedFilter, authorization, isAuthenticated) => {
  const [suggestionsData, setSuggestionsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [foundItems, setFoundItems] = useState([]);
  const [notFoundItems, setNotFoundItems] = useState([]);

  const baseEndpoint = `${API_URL}api/advancedsearch`;

  const filter =
    selectedFilter === 'geneSymbol' ? 'symbol' : selectedFilter === 'proteinStrID' ? 'strID' : selectedFilter;

  const normalizeDoId = value =>
    String(value || '')
      .replace(/^DO[_:]?/i, '')
      .trim();

  const fetchSuggestions = async inputs => {
    if (!filter) return;

    const validInputs = [];
    const invalidInputs = [];

    inputs.forEach(input => {
      if (!validateInput(input, searchType, filter)) {
        invalidInputs.push({ query: input, validationError: 'Invalid input format' });
      } else {
        validInputs.push(input);
      }
    });

    if (invalidInputs.length > 0) {
      setSuggestionsData(
        invalidInputs.map(item => ({
          query: item.query,
          found: false,
          error: item.validationError,
          results: [],
        })),
      );
    }

    if (validInputs.length === 0) {
      setError('No valid inputs provided.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let pattern = validInputs.join(',');

      if (searchType === 'DISEASES' && filter === 'name') {
        pattern = validInputs.map(i => encodeURIComponent(i)).join(',');
      }

      const response = await Axios.get(baseEndpoint, {
        params: {
          pattern,
          filter: filter,
          type: searchType,
          ...(isAuthenticated ? {} : { skey: 'unauth' }),
        },
        headers: {
          Authorization: authorization,
        },
      });

      const payload = response.data.payload || {};

      const allSuggestions = validInputs.map(input => {
        let isFound = false;
        let resultData = [];

        switch (searchType) {
          case 'DISEASES': {
            const diseases =
              filter === 'name' ? payload.normalizedEntities[0].payload : payload.diseases || [];
            switch (filter) {
              case 'diseaseUMLSCUI':
                resultData = diseases.filter(disease => disease.diseaseUMLSCUI === input);
                break;
              case 'MSH':
                resultData = diseases.filter(disease => disease.msh.includes(input));
                break;
              case 'OMIM':
                resultData = diseases.filter(disease => disease.omim.includes(input));
                break;
              case 'NCI':
                resultData = diseases.filter(disease => disease.nci.includes(input));
                break;
              case 'ORDO':
                resultData = diseases.filter(disease => disease.ordo.includes(input));
                break;
              case 'HPO':
                resultData = diseases.filter(disease => disease.hpo.includes(input));
                break;
              case 'MONDO':
                resultData = diseases.filter(disease => disease.mondo.includes(input));
                break;
              case 'EFO':
                resultData = diseases.filter(disease => disease.efo.includes(input));
                break;
              case 'name':
                resultData = diseases
                  .filter(disease => {
                    const inputLower = input.toLowerCase();
                    return (
                      (disease.normalizedName && disease.normalizedName.toLowerCase().includes(inputLower)) ||
                      (disease.matchedText && disease.matchedText.toLowerCase().includes(inputLower)) ||
                      (disease.term && disease.term.toLowerCase().includes(inputLower))
                    );
                  })
                  .map(disease => ({
                    ...disease,
                    term: disease.term || 'Not available',
                    normalizedName: disease.normalizedName || disease.term || 'Not available',
                    diseaseUMLSCUI: disease.diseaseUMLSCUI || 'Not available',
                  }));
                break;
              case 'DO':
                resultData = diseases.filter(
                  disease =>
                    Array.isArray(disease.do) &&
                    disease.do.some(doId => normalizeDoId(doId) === normalizeDoId(input)),
                );
                break;
              default:
                resultData = [];
                console.error(`Unknown filter type: ${filter}`);
                break;
            }
            isFound = resultData.length > 0;
            break;
          }
          case 'VARIANTS': {
            const variants = payload.variants || [];
            if (selectedFilter === 'strID') {
              resultData = variants.filter(variant => variant.strID.includes(input));
            } else if (selectedFilter === 'chromocoord') {
              resultData = variants.filter(variant => input.includes(variant.coord));
            } else if (selectedFilter === 'proteinStrID') {
              resultData = variants.filter(variant => {
                const normalizedInput = input.toLowerCase().replace(/\s+/g, ' ').trim();

                const hasOneLetter =
                  Array.isArray(variant.oneletterID) &&
                  variant.oneletterID.some(id =>
                    id.toLowerCase().replace(/\s+/g, ' ').trim().includes(normalizedInput),
                  );

                const hasThreeLetter =
                  Array.isArray(variant.threeletterID) &&
                  variant.threeletterID.some(id =>
                    id.toLowerCase().replace(/\s+/g, ' ').trim().includes(normalizedInput),
                  );

                return hasOneLetter || hasThreeLetter;
              });
            } else {
              resultData = [];
              console.error(`Unknown filter type: ${selectedFilter}`);
            }
            isFound = resultData.length > 0;
            break;
          }
          case 'GENES': {
            const genes =
              filter === 'name' ? payload.normalizedEntities?.[0]?.payload || [] : payload.genes || [];

            switch (filter) {
              case 'symbol':
                resultData = genes.filter(gene => gene.symbol === input);
                break;

              case 'ensemblID':
              case 'ensemblId':
                resultData = genes.filter(gene => gene.ensembl_ids?.[0]?.includes(input));
                break;

              case 'uniprotID':
              case 'uniprotId':
                resultData = genes.filter(gene => gene.proteinStrIDs?.[0]?.includes(input));
                break;

              case 'geneID':
              case 'geneId':
                resultData = genes.filter(gene => gene.geneID?.toString().includes(input));
                break;

              case 'name':
                resultData = genes
                  .filter(gene => {
                    const inputLower = input.toLowerCase();
                    return (
                      gene.term?.toLowerCase().includes(inputLower) ||
                      gene.normalizedName?.toLowerCase().includes(inputLower) ||
                      gene.matchedText?.toLowerCase().includes(inputLower)
                    );
                  })
                  .map(gene => ({
                    ...gene,
                    symbol: gene.symbol || gene.geneSymbol,
                    geneID: gene.geneID || gene.normalizedId,
                    enrichmentSymbol: gene.symbol || gene.geneSymbol,
                  }));
                break;

              default:
                resultData = [];
                console.error(`Unknown filter type: ${filter}`);
                break;
            }

            isFound = resultData.length > 0;
            break;
          }
          case 'CHEMICALS': {
            const chems = filter === 'name' ? payload.normalizedEntities[0].payload : payload.chemicals || [];
            switch (filter) {
              case 'CHEMBL':
                resultData = chems.filter(chem => chem.chemID === input);
                break;
              case 'CHEBI':
                resultData = chems.filter(chem => {
                  const chebi = chem.chebi[0].replace('__', '').split('__')[0];
                  return chebi.includes(input);
                });
                break;
              case 'DRUGBANK':
                resultData = chems.filter(chem => {
                  const db = !isEmpty(chem.drugbank)
                    ? chem.drugbank[0].replace('__', '').split('__')[0]
                    : null;
                  return db ? db.includes(input) : null;
                });
                break;
              case 'MESH':
                resultData = chems.filter(chem => {
                  const mesh = !isEmpty(chem.mesh) ? chem.mesh[0].replace('__', '').split('__')[0] : null;
                  return mesh ? mesh.includes(input) : null;
                });
                break;
              case 'PUBCHEM':
                resultData = chems.filter(chem => {
                  const pc = !isEmpty(chem.pubchem) ? chem.pubchem[0].replace('__', '').split('__')[0] : null;
                  return pc ? pc.includes(input) : null;
                });
                break;
              case 'name':
                resultData = chems
                  .filter(chem => {
                    const inputLower = input.toLowerCase();
                    return (
                      chem.normalizedName?.toLowerCase().includes(inputLower) ||
                      chem.matchedText?.toLowerCase().includes(inputLower) ||
                      chem.term?.toLowerCase().includes(inputLower)
                    );
                  })
                  .map(chem => ({
                    ...chem,
                    chemID: chem.normalizedId,
                  }));
                break;
              default:
                resultData = [];
                console.error(`Unknown filter type: ${filter}`);
                break;
            }
            isFound = resultData.length > 0;
            break;
          }
          default:
            break;
        }

        return {
          query: input,
          found: isFound,
          results: resultData,
        };
      });

      setSuggestionsData([...allSuggestions, ...invalidInputs]);
      setFoundItems(allSuggestions.filter(item => item.found).map(item => item.results[0]));
      setNotFoundItems(allSuggestions.filter(item => !item.found).map(item => item.query));
    } catch (err) {
      console.error('Error fetching suggestions:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return { suggestionsData, loading, error, foundItems, notFoundItems, fetchSuggestions };
};

export default useFetchSuggestions;
